import { inferCategoryFromMatterType, resolveMatterTypeId } from "./caseTaxonomyRegistry";
import { isWritLegacyMatterType } from "./writTaxonomy";
import { resolveLegacyCriminalProceeding } from "./criminalTaxonomy";

function inferCriminalCategoryFromProceeding(proceedingId?: string): string | undefined {
  if (!proceedingId) return undefined;
  return resolveLegacyCriminalProceeding(proceedingId)?.categoryId;
}

export type TopFilter =
  | "All"
  | "Civil"
  | "Criminal"
  | "Family"
  | "Writ"
  | "Rent"
  | "Revenue"
  | "Banking"
  | "Consumer"
  | "NAB"
  | "FIA"
  | "SupremeCourt"
  | "Other";

export type DraftsTopFilter = TopFilter | "Unlinked";

export const TOP_FILTER_OPTIONS: TopFilter[] = [
  "All",
  "Civil",
  "Criminal",
  "Family",
  "Rent",
  "Revenue",
  "Banking",
  "Consumer",
  "NAB",
  "FIA",
  "SupremeCourt",
  "Writ",
  "Other",
];

export const SUBFILTER_OPTIONS: Record<Exclude<TopFilter, "All" | "Other">, readonly string[]> = {
  Civil: ["All Civil", "Suits", "Appeals", "Revisions", "Executions", "Applications", "Other Civil"],
  Criminal: [
    "All Criminal",
    "Bail",
    "Trials",
    "Appeals",
    "Revisions",
    "Applications",
    "Complaints",
    "High Court",
    "Other Criminal",
  ],
  Family: ["All Family", "Suits", "Custody / Guardianship", "Executions", "Appeals", "Applications", "Other Family"],
  Writ: ["All Writ", "Writ Petitions", "ICA", "Contempt", "Other"],
  Rent: [
    "All Rent",
    "Eviction",
    "Recovery",
    "Rent Deposit",
    "Tenant Protection",
    "Appeals",
    "Revisions / Writs",
    "Applications",
    "Other Rent",
  ],
  Revenue: [
    "All Revenue",
    "Mutation",
    "Partition",
    "Record Correction",
    "Demarcation",
    "Possession",
    "Appeals",
    "Revisions",
    "Board of Revenue",
    "Land Acquisition",
    "Applications",
    "Other Revenue",
  ],
  Banking: [
    "All Banking",
    "Banking Suits",
    "Recovery",
    "Security Matters",
    "Finance Defaults",
    "Executions",
    "Appeals",
    "Revisions / Writs",
    "Applications",
    "Other Banking",
  ],
  Consumer: [
    "All Consumer",
    "Complaints",
    "Defective Goods",
    "Service Deficiency",
    "Professional Negligence",
    "Builder / Housing",
    "Utility / Online",
    "Appeals",
    "Execution / Compliance",
    "Applications",
    "Other Consumer",
  ],
  NAB: [
    "All NAB",
    "Inquiry / Call-Up",
    "Investigation",
    "References",
    "Bail",
    "Remand",
    "VR / Plea Bargain",
    "Freezing / Attachment",
    "Appeals",
    "Writs / Challenges",
    "Applications",
    "Other NAB",
  ],
  FIA: [
    "All FIA",
    "Inquiry / Notice",
    "Investigation",
    "Cybercrime / PECA",
    "Electronic Fraud",
    "Trafficking / Smuggling",
    "Immigration / Passport",
    "AML",
    "Challan / Trial",
    "Bail",
    "Remand",
    "Appeals",
    "Writs / Challenges",
    "Applications",
    "Other FIA",
  ],
  SupremeCourt: [
    "All Supreme Court",
    "CPLA",
    "Criminal CPLA",
    "SLP",
    "Human Rights",
    "Review",
    "Applications",
    "Other Supreme Court",
  ],
};

const SUBFILTER_CASE_TYPE: Record<
  Exclude<TopFilter, "All" | "Other">,
  Record<string, string>
> = {
  Civil: {
    Suits: "Civil Suit",
    Appeals: "Civil Appeal",
    Revisions: "Civil Revision",
    Executions: "Execution Petition",
    Applications: "Miscellaneous Civil Application",
    "Other Civil": "Other Civil Matter",
  },
  Criminal: {
    Bail: "Bail Matters",
    Trials: "Criminal Trial",
    Appeals: "Criminal Appeal",
    Revisions: "Criminal Revision",
    Applications: "Criminal Miscellaneous",
    Complaints: "Private Complaint",
    "High Court": "High Court Criminal Jurisdiction",
    "Other Criminal": "Other Criminal Matter",
  },
  Family: {
    Suits: "Family Suit",
    "Custody / Guardianship": "Guardianship / Custody",
    Executions: "Family Execution",
    Appeals: "Family Appeal",
    Applications: "Family Miscellaneous Application",
    "Other Family": "Other Family Matter",
  },
  Writ: {
    "Writ Petitions": "Writ Petition",
    ICA: "Intra-Court Appeal",
    Contempt: "Contempt Petition",
    Other: "Other Constitutional Matter",
  },
  Rent: {
    Eviction: "Eviction / Ejectment Petition",
    Recovery: "Rent Recovery",
    "Rent Deposit": "Rent Deposit / Consignment",
    "Tenant Protection": "Tenant Protection / Injunction",
    Appeals: "Rent Appeal",
    "Revisions / Writs": "Rent Revision / Constitutional Challenge",
    Applications: "Rent Miscellaneous Application",
    "Other Rent": "Other Rent / Tenancy Matter",
  },
  Revenue: {
    Mutation: "Mutation Matter",
    Partition: "Partition / Taqseem",
    "Record Correction": "Correction of Revenue Record",
    Demarcation: "Demarcation / Measurement",
    Possession: "Land Possession / Dispossession",
    Appeals: "Revenue Appeal",
    Revisions: "Revenue Revision",
    "Board of Revenue": "Board of Revenue Matter",
    "Land Acquisition": "Land Acquisition / Compensation",
    Applications: "Revenue Miscellaneous Application",
    "Other Revenue": "Other Revenue / Land Matter",
  },
  Banking: {
    "Banking Suits": "Banking Suit",
    Recovery: "Financial Recovery Suit",
    "Security Matters": "Mortgage / Pledge / Hypothecation Matter",
    "Finance Defaults": "Loan / Finance Default Matter",
    Executions: "Banking Execution",
    Appeals: "Banking Appeal",
    "Revisions / Writs": "Banking Revision / Constitutional Challenge",
    Applications: "Banking Miscellaneous Application",
    "Other Banking": "Other Banking / Financial Matter",
  },
  Consumer: {
    Complaints: "Consumer Complaint",
    "Defective Goods": "Defective Goods Claim",
    "Service Deficiency": "Deficiency in Service Claim",
    "Professional Negligence": "Medical Negligence / Professional Service Complaint",
    "Builder / Housing": "Builder / Developer / Housing Society Complaint",
    "Utility / Online": "Utility / Telecom / Online Service Complaint",
    Appeals: "Consumer Appeal",
    "Execution / Compliance": "Consumer Execution / Compliance",
    Applications: "Consumer Miscellaneous Application",
    "Other Consumer": "Other Consumer Matter",
  },
  NAB: {
    "Inquiry / Call-Up": "NAB Inquiry / Call-Up Notice Matter",
    Investigation: "NAB Investigation Matter",
    References: "NAB Reference / Trial",
    Bail: "NAB Bail",
    Remand: "Remand / Custody Matter",
    "VR / Plea Bargain": "Voluntary Return / Plea Bargain",
    "Freezing / Attachment": "Freezing / Attachment of Property",
    Appeals: "NAB Appeal",
    "Writs / Challenges": "NAB Revision / Constitutional Challenge",
    Applications: "NAB Miscellaneous Application",
    "Other NAB": "Other NAB / Accountability Matter",
  },
  FIA: {
    "Inquiry / Notice": "FIA Inquiry / Notice Matter",
    Investigation: "FIA Investigation Matter",
    "Cybercrime / PECA": "Cybercrime / PECA Matter",
    "Electronic Fraud": "Electronic Fraud / Financial Cybercrime",
    "Trafficking / Smuggling": "Human Trafficking / Migrant Smuggling Matter",
    "Immigration / Passport": "Immigration / Passport / Exit Control Matter",
    AML: "Anti-Money Laundering Matter",
    "Challan / Trial": "FIA Challan / Trial",
    Bail: "FIA Bail",
    Remand: "Remand / Custody Matter",
    Appeals: "FIA Appeal",
    "Writs / Challenges": "FIA Revision / Constitutional Challenge",
    Applications: "FIA Miscellaneous Application",
    "Other FIA": "Other FIA / Federal Offence Matter",
  },
  SupremeCourt: {
    CPLA: "Civil Petition for Leave to Appeal (CPLA)",
    "Criminal CPLA": "Criminal Petition for Leave to Appeal",
    SLP: "Special Leave Petition (SLP)",
    "Human Rights": "Human Rights Case (Article 184(3))",
    Review: "Review Petition",
    Applications: "Supreme Court Miscellaneous Application",
    "Other Supreme Court": "Other Supreme Court Matter",
  },
};

export function topFilterToDefaultSubFilter(top: TopFilter): string {
  if (top === "All" || top === "Other") return "All Civil";
  return SUBFILTER_OPTIONS[top][0];
}

/** Criminal sub-bar chip → taxonomy category id (e.g. Bail → Bail Matters). */
export function getCriminalCategoryIdForSubFilter(subFilter: string): string | undefined {
  return SUBFILTER_CASE_TYPE.Criminal[subFilter];
}

export function matchesCaseTaxonomy(
  norm: { caseCategory?: string; caseType?: string; criminalCategory?: string; matterType?: string },
  topFilter: TopFilter,
  subFilter: string,
): boolean {
  if (topFilter === "All") return true;

  const isCivil = norm.caseCategory === "Civil";
  const isCriminal = norm.caseCategory === "Criminal";
  const isFamily = norm.caseCategory === "Family";
  const isWrit = norm.caseCategory === "Constitutional / Writ";
  const isRent = norm.caseCategory === "Rent / Tenancy";
  const isRevenue = norm.caseCategory === "Revenue / Land";
  const isBanking = norm.caseCategory === "Banking / Financial Recovery";
  const isConsumer = norm.caseCategory === "Consumer Protection";
  const isNab = norm.caseCategory === "NAB / Accountability";
  const isFia = norm.caseCategory === "FIA / Cybercrime / Federal Offences";
  const isSupremeCourt = norm.caseCategory === "Supreme Court";

  if (topFilter === "Civil" && !isCivil) return false;
  if (topFilter === "Criminal" && !isCriminal) return false;
  if (topFilter === "Family" && !isFamily) return false;
  if (topFilter === "Writ" && !isWrit) return false;
  if (topFilter === "Rent" && !isRent) return false;
  if (topFilter === "Revenue" && !isRevenue) return false;
  if (topFilter === "Banking" && !isBanking) return false;
  if (topFilter === "Consumer" && !isConsumer) return false;
  if (topFilter === "NAB" && !isNab) return false;
  if (topFilter === "FIA" && !isFia) return false;
  if (topFilter === "SupremeCourt" && !isSupremeCourt) return false;
  if (
    topFilter === "Other" &&
    (isCivil || isCriminal || isFamily || isWrit || isRent || isRevenue || isBanking || isConsumer || isNab || isFia || isSupremeCourt)
  ) {
    return false;
  }

  if (topFilter === "Other") return true;

  const allLabel = SUBFILTER_OPTIONS[topFilter][0];
  if (subFilter === allLabel) return true;

  const expectedType = SUBFILTER_CASE_TYPE[topFilter][subFilter];
  if (!expectedType) return true;

  if (topFilter === "Criminal") {
    const criminalCategory =
      norm.criminalCategory ||
      (norm.caseCategory === "Criminal" ? inferCriminalCategoryFromProceeding(norm.caseType || norm.matterType) : undefined);
    return criminalCategory === expectedType;
  }

  if (topFilter === "Writ") {
    const caseType = norm.caseType || norm.matterType || "";
    if (expectedType === "Writ Petition") {
      return (
        caseType === "Writ Petition" ||
        isWritLegacyMatterType(caseType) ||
        caseType === "Constitutional Miscellaneous Application"
      );
    }
    if (expectedType === "Other Constitutional Matter") {
      return caseType === "Other Constitutional Matter" || caseType === "Other Constitutional / Writ Matter";
    }
    return caseType === expectedType;
  }

  return norm.caseType === expectedType;
}

/** Whether a template's matter-type tags match the library category bar (any tag may match). */
export function matchesTemplateTaxonomy(
  matterTypeIds: string[],
  topFilter: TopFilter,
  subFilter: string,
): boolean {
  if (topFilter === "All") return true;

  const types = matterTypeIds.map((t) => resolveMatterTypeId(t)).filter(Boolean);
  if (types.length === 0) return topFilter === "Other";

  return types.some((typeId) =>
    matchesCaseTaxonomy(
      {
        caseCategory: inferCategoryFromMatterType(typeId),
        caseType: typeId,
      },
      topFilter,
      subFilter,
    ),
  );
}
